import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Badge, Button, Card, EmptyState, ErrorAlert, PageHeader, Pagination, Spinner, StatusBadge } from '../components/ui';
import { QualityScores } from '../components/Quality';
import { formatDate, truncate } from '@kontentowo/shared';
import { Fragment, useState } from 'react';

export function ReviewQueuePage() {
  const { data, error, loading, reload } = useAsync(() => api.content.list({ status: 'REVIEW,GENERATED,DRAFT' }), []);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const [quality, setQuality] = useState<Record<string, { variantId: string; platform: string; scores: Record<string, unknown> }[]>>({});
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const act = async (id: string, fn: () => Promise<unknown>) => {
    setBusy(id);
    setActionError(null);
    try {
      await fn();
      reload();
    } catch (err) {
      setActionError(err);
    } finally {
      setBusy(null);
    }
  };

  const checkQuality = async (id: string) => {
    setBusy(id);
    setActionError(null);
    try {
      const res = await api.content.quality(id);
      setQuality((q) => ({ ...q, [id]: res }));
      reload();
    } catch (err) {
      setActionError(err);
    } finally {
      setBusy(null);
    }
  };

  const reviewable = data ?? [];

  return (
    <>
      <PageHeader title="Review Queue" subtitle="Zatwierdź, odrzuć lub edytuj wygenerowany content. Publikacja wymaga zatwierdzenia." />
      {loading && <Spinner />}
      {error && <ErrorAlert error={error} />}
      <ErrorAlert error={actionError} />

      {!loading && reviewable.length === 0 && <EmptyState icon="✓" title="Kolejka jest pusta" hint="Wygeneruj content w Content Studio." />}

      {reviewable.length > 0 && (
        <Card flush>
          <table className="table">
            <thead>
              <tr>
                <th>Temat</th>
                <th>Brand</th>
                <th>Status</th>
                <th>Język</th>
                <th>Updated</th>
                <th style={{ textAlign: 'right' }}>Akcje</th>
              </tr>
            </thead>
            <tbody>
              {reviewable.slice((page - 1) * pageSize, page * pageSize).map((item) => (
                <Fragment key={item.id}>
                <tr>
                  <td>
                    <Link to={`/content/${item.id}`}>{truncate(item.topic, 60)}</Link>
                  </td>
                  <td className="muted">{item.brand?.name ?? '—'}</td>
                  <td>
                    <StatusBadge status={item.status} />
                  </td>
                  <td className="muted">{item.language}</td>
                  <td className="muted">{formatDate(item.updatedAt)}</td>
                  <td>
                    <div className="btn-row" style={{ justifyContent: 'flex-end' }}>
                      <Button size="sm" disabled={busy === item.id} onClick={() => checkQuality(item.id)}>
                        Quality
                      </Button>
                      <Button size="sm" variant="success" disabled={busy === item.id} onClick={() => act(item.id, () => api.content.approve(item.id))}>
                        Approve
                      </Button>
                      <Button size="sm" variant="danger" disabled={busy === item.id} onClick={() => act(item.id, () => api.content.reject(item.id))}>
                        Reject
                      </Button>
                      <Link className="btn btn--sm" to={`/content/${item.id}`}>
                        Edit
                      </Link>
                    </div>
                  </td>
                </tr>
                {quality[item.id] && (
                  <tr>
                    <td colSpan={6} style={{ background: 'var(--surface-2)' }}>
                      <div className="stack">
                        {quality[item.id]!.map((r) => (
                          <div key={r.variantId} className="inline" style={{ alignItems: 'flex-start' }}>
                            <Badge tone="info">{r.platform}</Badge>
                            <QualityScores scores={r.scores as never} />
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
          <Pagination page={page} pageSize={pageSize} total={reviewable.length} onChange={setPage} />
        </Card>
      )}
    </>
  );
}
